import dayjs from 'dayjs'
import { reportError } from '@lib/errorHandler'
import { usePaginationStore } from '@app/stores/tables/PaginationStore'

// Factory for a b-table provider function that can be passed into the table :items prop.
// It expects this to be a Vue component instance, so you should wire it up as a method like:
// methods: tableProvider(apiFunction, ['arg1ForApiFunction', 'arg2ForApiFunction'], { someApiParam: 123 }, mapFunction)
//
// It's assumed that whatever component you wire this up to has this.totalRows and this.updateFilterOptions
// That's a smell which should be eliminated, but haven't quite figured out a better approach yet.
//
// getFunction is an API function following the usual format from api.js, but the last argument
// must be a params object that specifies search params like filter, page, and per_page.
//
// getArgs is an array of strings specifying what args to pass to API function. Each string
// should represent a property on the Vue component instance e.g. a prop or computed.
//
// extraParams is an object that will be merged into the params object passed to the API function.
//
// mapFunction is a function that takes the API response and returns an array of table items
//
// Note that the keys in the object it returns must match the keys defined in Searchkick
// since these are used to generate the sort_by API param. Specifically, make sure the keys
// match what is defined in the `search_data` defined on the model. The generated provider function
// will then include sorting params in the API request automatically.
// See: https://bootstrap-vue.org/docs/components/table#items-record-data
/**
 *
 * @param getFunction
 * @param getArgs
 * @param extraParams
 * @param mapFunction
 */
export function generateTableProvider(getFunction, getArgs, extraParams, mapFunction) {
  // This returned function is the b-table provider function as specified here:
  // https://bootstrap-vue.org/docs/components/table#using-items-provider-functions
  return function (ctx) {
    const tableParams = {
      ...ctx.filter,
      page: ctx.currentPage,
      per_page: ctx.perPage,
      sort_by: ctx.sortBy || null,
      // It doesn't make sense that we pass a boolean as sort_dir. Currently we
      // do that and fix to be 'asc'/'desc' on the backend. Leaving as is for now but
      // this should be fixed.
      // Once backend hack is removed this would be `sort_dir: ctx.sortDesc ? 'desc' : 'asc'`,
      sort_dir: ctx.sortDesc,
    }

    const args = getArgs.map((arg) => this[arg])
    const params = { ...extraParams, ...tableParams }
    args.push(params)
    return getFunction(...args)
      .then((response) => {
        usePaginationStore().totalRows = response.data.pagination.total
        if (this.updateFilterOptions) {
          this.updateFilterOptions(response.data)
          // We expect the component which imports this to have a tableData property
          // which we'll use to stash the full response data.
          this.tableData = response.data
        }

        return mapFunction(response.data)
      })
      .catch((e) => {
        reportError(e, 'generateTableProvider', 'lib')
        alert('Error fetching table data')
        console.error(e)
        return []
      })
  }
}

/*
 * This interface defines the shape of the context object which the Vue Bootstrap b-table
 * will pass to the table provider.
 */
interface TableContext {
  filter: object
  currentPage?: number
  perPage?: number
  sortBy?: string
  sortDesc?: boolean
}

/*
 * Describes generic properties of an axios response
 */
interface Response {
  data: unknown
}

// Upgrade version of generateTableProvide that doesn't have as much nasty coupling
// This should replace the old one within the next couple weeks, creating it as new for now
// to avoid nasty merge conflicts due to related ongoing work.
/**
 *
 * @param getFunction
 * @param getArgs
 * @param extraParams
 * @param mapFunction
 * @param updateFilterOptions
 */
export function generateTableProviderNew(
  getFunction: (...args: unknown[]) => Promise<Response>,
  getArgs: Array<unknown>,
  extraParams: object,
  mapFunction: (argo0: unknown) => Array<unknown>,
  updateFilterOptions: (arg0: unknown) => void,
) {
  // This returned function is the b-table provider function as specified here:
  // https://bootstrap-vue.org/docs/components/table#using-items-provider-functions
  return function (ctx: TableContext) {
    const tableParams = {
      ...ctx.filter,
      page: ctx.currentPage,
      per_page: ctx.perPage,
      sort_by: ctx.sortBy || null,
      // It doesn't make sense that we pass a boolean as sort_dir. Currently we
      // do that and fix to be 'asc'/'desc' on the backend. Leaving as is for now but
      // this should be fixed.
      // Once backend hack is removed this would be `sort_dir: ctx.sortDesc ? 'desc' : 'asc'`,
      sort_dir: ctx.sortDesc,
    }

    const args = [...getArgs]
    const params = { ...extraParams, ...tableParams }
    args.push(params)
    return getFunction(...args)
      .then((response: Response) => {
        if (updateFilterOptions) {
          updateFilterOptions(response.data)
        }

        usePaginationStore().totalRows = response.data.pagination.total

        return mapFunction(response.data)
      })
      .catch((e) => {
        reportError(e, 'generateTableProvider', 'lib')
        alert('Error fetching table data')
        console.error(e)
        return []
      })
  }
}

// Generate filter options for a b-form-select component
// param filters: An array of filter objects from API response
// param allOptionName: The name for the "All" option e.g. "All Companies"
// returns an object suitable for passing to :options of a b-form-select
export interface FilterOption {
  id: string
  name: string
  count: number
}

/**
 *
 * @param filters
 * @param allOptionName
 */
export function generateFilterOptions(filters: FilterOption[], allOptionName: string) {
  if (filters === undefined || filters === null) {
    console.warn(`No filters passed for generation`)
    return []
  }

  const options = [
    ...filters.map((f) => ({
      value: f.id,
      text: f.count != null ? `${f.name} (${f.count})` : f.name,
    })),
  ]

  if (allOptionName) {
    return [{ value: '', text: allOptionName }, ...options]
  } else {
    return options
  }
}

// Standard format for date shown in table column
/**
 *
 * @param date
 * @param format
 */
export function formatDateCell(date, format = 'MMM DD, YYYY') {
  return date ? dayjs(date).format(format) : ''
}

// Adds some spacing between question types for better readability
// and apply some renaming on some types ie: Matrixx and ClassifierMcoQuestion
/**
 *
 * @param questionType
 */
export function formatQuestionTypeText(questionType) {
  switch (questionType) {
    case 'MultipleChoiceOne':
      return 'Multiple Choice One'
    case 'MultipleChoiceMany':
      return 'Multiple Choice Many'
    case 'Matrixx':
      return 'Matrix'
    case 'ReferenceVolunteer':
      return 'Reference Volunteer'
    case 'NamedAttribution':
      return 'Named Attribution'
    case 'ClassifierMcoQuestion':
      return 'Classifier'
    case 'MatrixxRow':
      return 'Matrix Row'
    default:
      return questionType
  }
}

// Returns a class name based on the NPS score
/**
 *
 * @param npsScore
 */
export function npsClass(npsScore) {
  if (npsScore < 7) return 'negative'
  else if (npsScore < 9) return 'neutral'
  else return 'positive'
}
